import { useInfiniteQuery } from 'react-query'
import axios from 'utils/axios'
import { DTGVariantsPageDto } from '@merchx-v2/shared-types'
import { guardFromErrors, extractErrorInfo } from 'utils/graphqlHelpers'

const DEFAULT_PAGE_SIZE = 30

/// ////////////////////////////////////////////////////////////////////////////////
// TYPES
/// ////////////////////////////////////////////////////////////////////////////////

export type DTGVariantsPage = DTGVariantsPageDto & {
  currentPage: number
  pageSize: number
}

/// ////////////////////////////////////////////////////////////////////////////////
// GRAPHQL
/// ////////////////////////////////////////////////////////////////////////////////

type QueryResponse = GraphQLResponse<'DTGVariantsListPage', DTGVariantsPage>

const FETCH_DTG_VARRIANTS_LIST = `
  query fetchDTGVariantsList($page: Int, $size: Int) {
    DTGVariantsListPage(page: $page, size: $size) {
      items {
        id
        DTGProductId
        DTGProduct {
          name
        }
        externalVariantId
        variantKey
        createdAt
        updatedAt
      }
      total
    }
  }
`

/// ////////////////////////////////////////////////////////////////////////////////
// FUNCTION
/// ////////////////////////////////////////////////////////////////////////////////

type PageParam = {
  page?: number
  size?: number
  filters?: {
    limit?: number
    searchText?: string
  }
}

type FetchDTGVariantsListArgs = {
  queryKey: string | string[]
  pageParam?: PageParam
}

type FetchDTGVariantsList = (args: FetchDTGVariantsListArgs) => Promise<DTGVariantsPage>

const fetchDTGVariantsList: FetchDTGVariantsList = async ({ pageParam }) => {
  const { page = 1, size = DEFAULT_PAGE_SIZE, filters = undefined } = pageParam || {}
  try {
    const {
      data: { data, errors }
    }: QueryResponse = await axios.post('/graphql', {
      query: FETCH_DTG_VARRIANTS_LIST,
      variables: { page, size, filters }
    })

    guardFromErrors(errors)

    if (!data) {
      throw new Error('Response body is empty!')
    }

    const { DTGVariantsListPage } = data
    if (!DTGVariantsListPage) {
      throw new Error("Can't get DTGVariants list page!")
    }

    return {
      items: DTGVariantsListPage.items,
      total: DTGVariantsListPage.total,
      currentPage: page,
      pageSize: size,
      filters
    }
  } catch (error) {
    throw new Error(extractErrorInfo(error))
  }
}

/// ////////////////////////////////////////////////////////////////////////////////
// HOOK
/// ////////////////////////////////////////////////////////////////////////////////

type GetNextPageParam = (args: DTGVariantsPage) => PageParam | undefined

type DTGVariantsListEvents = {
  keepPreviousData: boolean
  getPreviousPageParam: GetNextPageParam
  getNextPageParam: GetNextPageParam
}

export const DTGVariantsListEvents: DTGVariantsListEvents = {
  keepPreviousData: true,
  getPreviousPageParam: ({ pageSize = DEFAULT_PAGE_SIZE, currentPage = 0 }) => {
    if (currentPage > 1) {
      return {
        page: currentPage - 1,
        size: pageSize
      }
    }

    return undefined
  },
  getNextPageParam: ({ pageSize = DEFAULT_PAGE_SIZE, currentPage = 0, total = 0 }) => {
    if (pageSize * currentPage < total) {
      return {
        page: currentPage + 1,
        size: pageSize
      }
    }

    return undefined
  }
}

export const useDTGVariantsList = () => {
  const cacheKey = ['DTGVariantsList']

  return useInfiniteQuery(
    cacheKey,
    prev =>
      fetchDTGVariantsList({
        queryKey: cacheKey,
        pageParam: { ...prev.pageParam }
      }),
    DTGVariantsListEvents
  )
}
